import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ModuleHeaderStrip from '../../../components/common/ModuleHeaderStrip';

const DEFAULT_CLASSES = [
  'Pre-Primary (Nursery)', 'Pre-Primary (LKG)', 'Pre-Primary (UKG)',
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
  'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
  'Class 11 (Science)', 'Class 11 (Commerce)', 'Class 11 (Arts)',
  'Class 12 (Science)', 'Class 12 (Commerce)', 'Class 12 (Arts)',
];
const SECTION_OPTIONS = ['A', 'B', 'C', 'D', 'E'];

export default function AttendancePage() {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const canWrite = ['admin', 'manager', 'team_lead', 'faculty', 'teacher', 'trainer'].includes(role) || user?.userType === 'super_admin';

  const ALL_TABS = [
    { key: 'take',    label: 'Take Attendance',   icon: 'bi-pencil-square', writeOnly: true },
    { key: 'summary', label: 'Class Summary',      icon: 'bi-bar-chart' },
    { key: 'daywise', label: 'Day-wise Report',    icon: 'bi-calendar3' },
    { key: 'leave',   label: 'Permission / Leave', icon: 'bi-calendar-x' },
  ];
  const TABS = ALL_TABS.filter(t => !t.writeOnly || canWrite);

  const [activeTab, setActiveTab] = useState(() => canWrite ? 'take' : 'summary');
  const [classOptions, setClassOptions] = useState(DEFAULT_CLASSES);
  const [sectionOptions, setSectionOptions] = useState(['A', 'B', 'C', 'D', 'E']);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    api.get('/records/school_config', { params: { limit: 1 } })
      .then(r => {
        const rec = r.data?.records?.[0]?.recordData || r.data?.record?.recordData;
        if (rec?.classes?.length) setClassOptions(rec.classes);
        if (rec?.sections?.length) setSectionOptions(rec.sections);
      }).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader title="Attendance Management" icon="bi-calendar-check" subtitle="Track and manage student attendance" />

      <ModuleHeaderStrip
        icon="bi-calendar-check"
        label="Attendance"
        chips={[
          { label: 'Today', value: new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' }) },
          { label: 'Classes', value: classOptions.length ? classOptions.join(', ') : '—' },
          { label: 'Sections', value: sectionOptions.length ? sectionOptions.join(', ') : '—' },
          { label: 'Modes', value: 'Full-day · Period-wise' },
        ]}
        variant="info"
      />

      <ul className="nav nav-tabs mb-3">
        {TABS.map(t => (
          <li className="nav-item" key={t.key}>
            <button className={`nav-link ${activeTab === t.key ? 'active fw-semibold' : ''}`} onClick={() => setActiveTab(t.key)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {activeTab === 'take'    && <TakeAttendance    classOptions={classOptions} sectionOptions={sectionOptions} setToast={setToast} />}
      {activeTab === 'summary' && <AttendanceSummary classOptions={classOptions} sectionOptions={sectionOptions} />}
      {activeTab === 'daywise' && <DaywiseReport     classOptions={classOptions} sectionOptions={sectionOptions} />}
      {activeTab === 'leave'   && <PermissionLeave   classOptions={classOptions} sectionOptions={sectionOptions} setToast={setToast} />}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}

// ── Take Attendance ──────────────────────────────────────────────────────────
function TakeAttendance({ classOptions, sectionOptions, setToast }) {
  const [date, setDate]               = useState(new Date().toISOString().split('T')[0]);
  const [classGrade, setClassGrade]   = useState('');
  const [section, setSection]         = useState('');
  const [classTeacher, setClassTeacher] = useState('');
  const [students, setStudents]       = useState([]);
  const [attendance, setAttendance]   = useState({});
  const [notes, setNotes]             = useState({});
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  // Period-wise vs full-day. mode='full_day' is the default; mode='period'
  // requires a period number 1-12. Each (class, section, date, period) can be
  // marked independently so a teacher can record period-1 attendance, then
  // come back at lunch to mark period-4 etc.
  const [mode, setMode]               = useState('full_day');
  const [period, setPeriod]           = useState(1);
  const [subject, setSubject]         = useState('');
  // Auto-populated from the timetable grid if available — shows what subject
  // is scheduled for the current (class, section, day, period).
  const [autoSubject, setAutoSubject] = useState('');

  const loadStudents = useCallback(async () => {
    if (!classGrade) return;
    setLoading(true);
    const params = { limit: 200 };
    if (classGrade) params['recordData.class_grade'] = classGrade;
    if (section)    params['recordData.section'] = section;
    const r = await api.get('/records/student_management', { params })
      .catch(() => ({ data: { records: [] } }));
    const studs = r.data.records || [];
    setStudents(studs);
    const init = {};
    studs.forEach(s => { init[s._id] = 'present'; });
    setAttendance(init);
    setNotes({});
    setLoading(false);
    // Try fetching class teacher from timetable
    api.get('/records/timetable', { params: { limit: 1, 'recordData.class_grade': classGrade } })
      .then(r2 => {
        const t = r2.data?.records?.[0]?.recordData;
        if (t?.teacher_name) setClassTeacher(t.teacher_name);
        else setClassTeacher('');
      }).catch(() => setClassTeacher(''));
  }, [classGrade, section]);

  // Auto-fetch the subject scheduled for (class, section, today, period) from
  // the structured staff_timetable grid. Lets period-wise attendance default
  // its subject without the teacher having to type it again.
  useEffect(() => {
    if (mode !== 'period' || !classGrade) { setAutoSubject(''); return; }
    const today = new Date(date);
    const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });
    api.get('/education/timetable/grid', {
      params: { classGrade, section: section || '' },
    })
      .then(r => {
        const rec = r.data?.data || r.data;
        const sched = rec?.recordData?.schedule || [];
        const cell = sched.find(s => s.day === dayName && Number(s.period) === Number(period));
        setAutoSubject(cell?.subject || '');
      })
      .catch(() => setAutoSubject(''));
  }, [mode, period, classGrade, section, date]);

  const markAll = (status) => {
    const updated = {};
    students.forEach(s => { updated[s._id] = status; });
    setAttendance(updated);
  };

  const saveAttendance = async () => {
    if (!classGrade || !date || students.length === 0) {
      setToast({ show: true, message: 'Please select class, date and load students first', type: 'warning' });
      return;
    }
    setSaving(true);
    const records = students.map(s => ({
      student_id: s._id,
      student_name: `${s.recordData?.first_name || ''} ${s.recordData?.last_name || ''}`.trim(),
      roll_number: s.recordData?.roll_number,
      attendance_status: attendance[s._id] || 'absent',
      note: notes[s._id] || '',
    }));
    try {
      // Pass `mode`, `period` and `subject` so the backend stores period-wise
      // records separately and includes the subject in parent-notification text.
      const payload = {
        date, class_grade: classGrade, section, records,
        mode,
        period:  mode === 'period' ? period   : null,
        subject: mode === 'period' ? (subject || autoSubject || '') : '',
      };
      const r = await api.post('/education/attendance/bulk', payload);
      const label = mode === 'period' ? `Period ${period}` : 'Full-day';
      const notified = r.data?.data?.parentsNotified || r.data?.parentsNotified || 0;
      setToast({
        show: true,
        message: `${label} attendance saved.${notified > 0 ? ` Notifying ${notified} parent(s) of absence/leave.` : ''}`,
        type: 'success',
      });
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to save attendance', type: 'danger' });
    } finally { setSaving(false); }
  };

  const STATUS_COLORS = { present: '#198754', absent: '#dc3545', leave: '#fd7e14', late: '#6610f2' };
  const counts = { present: 0, absent: 0, leave: 0, late: 0 };
  Object.values(attendance).forEach(v => { if (counts[v] !== undefined) counts[v]++; });

  return (
    <>
      {/* Mode picker — full-day (class teacher) vs period-wise (subject teacher) */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="fw-semibold small text-uppercase text-muted me-1">Attendance Mode:</span>
            <div className="btn-group btn-group-sm" role="group">
              <button type="button" className={`btn ${mode === 'full_day' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setMode('full_day')}
                title="Full-day attendance — for class teachers">
                <i className="bi bi-calendar-day me-1"/>Full-Day <span className="badge bg-light text-dark ms-1" style={{ fontSize: 9 }}>Class Teacher</span>
              </button>
              <button type="button" className={`btn ${mode === 'period' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setMode('period')}
                title="Per-period / subject attendance — for subject teachers">
                <i className="bi bi-clock me-1"/>Subject / Period <span className="badge bg-light text-dark ms-1" style={{ fontSize: 9 }}>Subject Teacher</span>
              </button>
            </div>
            {mode === 'period' && (
              <>
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label small mb-0">Period:</label>
                  <select className="form-select form-select-sm" style={{ width: 90 }}
                    value={period} onChange={e => setPeriod(parseInt(e.target.value, 10) || 1)}>
                    {Array.from({ length: 10 }, (_, i) => i + 1).map(p => (
                      <option key={p} value={p}>P {p}</option>
                    ))}
                  </select>
                </div>
                <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: 320 }}>
                  <label className="form-label small mb-0">Subject:</label>
                  <input className="form-control form-control-sm"
                    placeholder={autoSubject || 'e.g. Mathematics'}
                    value={subject}
                    onChange={e => setSubject(e.target.value)} />
                  {autoSubject && !subject && (
                    <button type="button" className="btn btn-outline-secondary btn-sm py-0 px-2"
                      onClick={() => setSubject(autoSubject)} title="Use the timetable's subject">
                      <i className="bi bi-magic"/>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="d-flex align-items-center gap-2 mt-2 small flex-wrap">
            <span className="text-muted">
              {mode === 'full_day'
                ? 'Class teacher marks one record per student for the day.'
                : `Subject teacher marks per-period attendance${autoSubject ? ` — scheduled subject: ${autoSubject}` : ''}.`}
            </span>
            <span className="badge bg-warning-subtle text-warning ms-auto">
              <i className="bi bi-bell me-1"/>Parents auto-notified for absent / leave
            </span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label fw-semibold">Date *</label>
              <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Class / Grade *</label>
              <select className="form-select" value={classGrade}
                onChange={e => { setClassGrade(e.target.value); setStudents([]); setClassTeacher(''); }}>
                <option value="">Select class</option>
                {classOptions.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Section</label>
              <select className="form-select" value={section} onChange={e => setSection(e.target.value)}>
                <option value="">All sections</option>
                {sectionOptions.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-primary w-100" onClick={loadStudents} disabled={!classGrade || loading}>
                {loading
                  ? <span className="spinner-border spinner-border-sm"></span>
                  : <><i className="bi bi-people me-1"></i>Load Students</>}
              </button>
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" onClick={saveAttendance} disabled={saving || students.length === 0}>
                {saving
                  ? <span className="spinner-border spinner-border-sm"></span>
                  : <><i className="bi bi-check2-all me-1"></i>Save {mode === 'period' ? `P${period}` : 'Day'}</>}
              </button>
            </div>
          </div>
          {classTeacher && (
            <div className="mt-2" style={{ fontSize: 13, color: '#6366f1' }}>
              <i className="bi bi-person-badge me-2"></i>
              <strong>Class Teacher:</strong> {classTeacher}
            </div>
          )}
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {!loading && students.length === 0 && (
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            <i className="bi bi-people display-4 d-block mb-3 opacity-25"></i>
            Select a class and click "Load Students" to mark attendance
          </div>
        </div>
      )}

      {students.length > 0 && (
        <>
          {/* Summary + Mark-all */}
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            {[
              { label: 'Present', color: '#198754', bg: '#d1fae5' },
              { label: 'Absent',  color: '#dc3545', bg: '#fee2e2' },
              { label: 'Leave',   color: '#fd7e14', bg: '#fff3cd' },
              { label: 'Late',    color: '#6610f2', bg: '#ede9fe' },
              { label: 'Total',   color: '#0ea5e9', bg: '#e0f2fe', count: students.length },
            ].map(item => (
              <div key={item.label} className="px-3 py-2 rounded text-center"
                style={{ background: item.bg, minWidth: 70 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: item.color }}>
                  {item.count !== undefined ? item.count : counts[item.label.toLowerCase()]}
                </div>
                <div style={{ fontSize: 11, color: item.color, fontWeight: 600 }}>{item.label}</div>
              </div>
            ))}
            <div className="ms-auto d-flex align-items-center gap-2 flex-wrap">
              <span style={{ fontSize: 12, color: '#6c757d' }}>Mark all:</span>
              {['present', 'absent', 'leave'].map(s => (
                <button key={s} className="btn btn-sm"
                  style={{ background: `${STATUS_COLORS[s]}20`, color: STATUS_COLORS[s], border: `1px solid ${STATUS_COLORS[s]}40`, textTransform: 'capitalize' }}
                  onClick={() => markAll(s)}>{s}</button>
              ))}
            </div>
          </div>

          {/* Attendance Sheet */}
          <div className="card">
            <div className="card-body p-0">
              <table className="table table-sm table-hover mb-0">
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Student Name</th>
                    <th>Roll No.</th>
                    <th>Status</th>
                    <th>Remark (optional)</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, i) => {
                    const status = attendance[s._id] || 'absent';
                    return (
                      <tr key={s._id}
                        style={{ background: status === 'absent' ? '#fff5f5' : status === 'present' ? '#f0fff4' : 'transparent' }}>
                        <td className="text-muted" style={{ fontSize: 12 }}>{i + 1}</td>
                        <td>
                          <span className="fw-medium">
                            {s.recordData?.first_name} {s.recordData?.last_name}
                          </span>
                        </td>
                        <td><code style={{ fontSize: 11 }}>{s.recordData?.roll_number || '—'}</code></td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            {Object.entries(STATUS_COLORS).map(([opt, color]) => (
                              <button key={opt} className="btn btn-sm"
                                style={{
                                  background: status === opt ? color : `${color}15`,
                                  color: status === opt ? '#fff' : color,
                                  border: `1px solid ${color}40`,
                                  padding: '2px 8px', fontSize: 11,
                                  textTransform: 'capitalize',
                                  fontWeight: status === opt ? 700 : 400,
                                }}
                                onClick={() => setAttendance(prev => ({ ...prev, [s._id]: opt }))}
                              >{opt}</button>
                            ))}
                          </div>
                        </td>
                        <td>
                          {(status === 'leave' || status === 'late' || status === 'absent') && (
                            <input type="text" className="form-control form-control-sm"
                              placeholder="reason…" style={{ fontSize: 11, maxWidth: 160 }}
                              value={notes[s._id] || ''}
                              onChange={e => setNotes(n => ({ ...n, [s._id]: e.target.value }))} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Class-wise Summary ────────────────────────────────────────────────────────
function AttendanceSummary({ classOptions, sectionOptions }) {
  const [classGrade, setClassGrade] = useState('');
  const [section,    setSection]    = useState('');
  const [month,      setMonth]      = useState(new Date().toISOString().slice(0, 7));
  const [summary,    setSummary]    = useState([]);
  const [loading,    setLoading]    = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { month, limit: 200 };
      if (classGrade) params.class_grade = classGrade;
      if (section)    params.section = section;
      const r = await api.get('/education/attendance/summary', { params });
      setSummary(r.data?.summary || r.data?.records || []);
    } catch { setSummary([]); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Class / Grade</label>
              <select className="form-select" value={classGrade} onChange={e => setClassGrade(e.target.value)}>
                <option value="">All Classes</option>
                {classOptions.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Section</label>
              <select className="form-select" value={section} onChange={e => setSection(e.target.value)}>
                <option value="">All</option>
                {sectionOptions.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Month</label>
              <input type="month" className="form-control" value={month} onChange={e => setMonth(e.target.value)} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-primary w-100" onClick={load} disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-graph-up me-1"></i>Load</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : summary.length === 0 ? (
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            <i className="bi bi-bar-chart display-4 d-block mb-3 opacity-25"></i>
            Select filters and click Load to view attendance summary
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header fw-semibold" style={{ fontSize: 13 }}>
            Attendance Summary — {month}
          </div>
          <div className="card-body p-0">
            <table className="table table-sm table-hover mb-0">
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th>Student</th><th>Class</th><th>Present</th><th>Absent</th>
                  <th>Leave</th><th>Total Days</th><th>Attendance %</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row, i) => {
                  const pct = row.total ? Math.round((row.present / row.total) * 100) : 0;
                  const pctColor = pct >= 75 ? '#198754' : pct >= 50 ? '#fd7e14' : '#dc3545';
                  return (
                    <tr key={i}>
                      <td className="fw-medium">{row.student_name || row.name}</td>
                      <td style={{ fontSize: 12 }}>{row.class_grade || '—'}</td>
                      <td><span className="badge bg-success bg-opacity-75">{row.present || 0}</span></td>
                      <td><span className="badge bg-danger bg-opacity-75">{row.absent || 0}</span></td>
                      <td><span className="badge bg-warning text-dark">{row.leave || 0}</span></td>
                      <td>{row.total || 0}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ height: 6, width: 80, background: '#e2e8f0', borderRadius: 3 }}>
                            <div style={{ height: 6, width: `${pct}%`, background: pctColor, borderRadius: 3 }}></div>
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: pctColor }}>{pct}%</span>
                          {pct < 75 && <span className="badge" style={{ background: '#fee2e2', color: '#dc3545', fontSize: 9 }}>Low</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Day-wise Report ───────────────────────────────────────────────────────────
function DaywiseReport({ classOptions, sectionOptions }) {
  const [classGrade, setClassGrade] = useState('');
  const [section,    setSection]    = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [report,  setReport]  = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate, limit: 100 };
      if (classGrade) params.class_grade = classGrade;
      if (section)    params.section = section;
      const r = await api.get('/education/attendance', { params });
      setReport(r.data?.records || []);
    } catch { setReport([]); }
    finally { setLoading(false); }
  };

  // Group by date
  const byDate = report.reduce((acc, rec) => {
    const d = rec.recordData?.date || rec.date;
    if (!d) return acc;
    if (!acc[d]) acc[d] = { total: 0, present: 0, absent: 0, leave: 0, late: 0 };
    const entries = rec.recordData?.records || [];
    entries.forEach(e => {
      acc[d].total++;
      if (acc[d][e.attendance_status] !== undefined) acc[d][e.attendance_status]++;
    });
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort();

  return (
    <div>
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Class / Grade</label>
              <select className="form-select" value={classGrade} onChange={e => setClassGrade(e.target.value)}>
                <option value="">All Classes</option>
                {classOptions.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Section</label>
              <select className="form-select" value={section} onChange={e => setSection(e.target.value)}>
                <option value="">All</option>
                {sectionOptions.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">From Date</label>
              <input type="date" className="form-control" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">To Date</label>
              <input type="date" className="form-control" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-primary w-100" onClick={load} disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-calendar3 me-1"></i>Load</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : dates.length === 0 ? (
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            <i className="bi bi-calendar3 display-4 d-block mb-3 opacity-25"></i>
            Select filters and click Load to view the day-wise report
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <table className="table table-sm table-hover mb-0">
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th>Date</th><th>Day</th><th>Total</th>
                  <th>Present</th><th>Absent</th><th>Leave</th><th>Late</th><th>Attendance %</th>
                </tr>
              </thead>
              <tbody>
                {dates.map(d => {
                  const row = byDate[d];
                  const pct = row.total ? Math.round((row.present / row.total) * 100) : 0;
                  const pctColor = pct >= 75 ? '#198754' : pct >= 50 ? '#fd7e14' : '#dc3545';
                  return (
                    <tr key={d}>
                      <td className="fw-semibold">{new Date(d).toLocaleDateString()}</td>
                      <td style={{ color: '#64748b' }}>
                        {new Date(d).toLocaleDateString('en-US', { weekday: 'short' })}
                      </td>
                      <td>{row.total}</td>
                      <td><span className="badge bg-success bg-opacity-75">{row.present}</span></td>
                      <td><span className="badge bg-danger bg-opacity-75">{row.absent}</span></td>
                      <td><span className="badge bg-warning text-dark">{row.leave}</span></td>
                      <td><span className="badge" style={{ background: '#ede9fe', color: '#6610f2' }}>{row.late}</span></td>
                      <td>
                        <span style={{ fontWeight: 700, color: pctColor }}>{pct}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Permission / Leave ────────────────────────────────────────────────────────
function PermissionLeave({ classOptions, sectionOptions, setToast }) {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const blank = { student_name: '', class_grade: '', section: '', request_type: 'Leave', from_date: '', to_date: '', reason: '', parent_phone: '' };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get('/records/permission_leave', { params: { limit: 50 } })
      .catch(() => ({ data: { records: [] } }));
    setRequests(r.data.records || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/records/permission_leave', form);
      setToast({ show: true, message: 'Leave request submitted!', type: 'success' });
      setShowForm(false); setForm(blank); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error submitting', type: 'danger' });
    } finally { setSaving(false); }
  };

  const handleStatus = async (id, status) => {
    await api.put(`/records/permission_leave/${id}`, { status }).catch(() => {});
    load();
  };

  const sf = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const STATUS_COLORS = { Approved: '#198754', Rejected: '#dc3545', Pending: '#f59e0b' };

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(s => !s)}>
          <i className="bi bi-plus-lg me-1"></i>New Request
        </button>
      </div>

      {showForm && (
        <div className="card mb-3" style={{ border: '2px solid #c7d2fe', borderRadius: 12 }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f5f3ff' }}>
            <span className="fw-semibold" style={{ fontSize: 14 }}>
              <i className="bi bi-calendar-x me-2 text-primary"></i>Leave / Permission Request
            </span>
            <button className="btn-close" onClick={() => setShowForm(false)}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Student Name *</label>
                  <input className="form-control" required value={form.student_name} onChange={sf('student_name')} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Class</label>
                  <select className="form-select" value={form.class_grade} onChange={sf('class_grade')}>
                    <option value="">Select class</option>
                    {classOptions.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Section</label>
                  <select className="form-select" value={form.section} onChange={sf('section')}>
                    <option value="">—</option>
                    {sectionOptions.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Request Type</label>
                  <select className="form-select" value={form.request_type} onChange={sf('request_type')}>
                    <option>Leave</option><option>Permission</option>
                    <option>Medical Leave</option><option>Planned Leave</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">From Date *</label>
                  <input type="date" className="form-control" required value={form.from_date} onChange={sf('from_date')} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">To Date</label>
                  <input type="date" className="form-control" value={form.to_date} onChange={sf('to_date')} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Parent Phone</label>
                  <input type="tel" className="form-control" value={form.parent_phone} onChange={sf('parent_phone')} />
                </div>
                <div className="col-12">
                  <label className="form-label">Reason *</label>
                  <textarea className="form-control" rows={2} required value={form.reason} onChange={sf('reason')}></textarea>
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : requests.length === 0 ? (
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            <i className="bi bi-calendar-x display-4 d-block mb-3 opacity-25"></i>
            No leave or permission requests yet
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <table className="table table-sm table-hover mb-0">
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th>Student</th><th>Class</th><th>Type</th>
                  <th>From</th><th>To</th><th>Reason</th><th>Status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(req => {
                  const d = req.recordData || {};
                  const st = d.status || 'Pending';
                  return (
                    <tr key={req._id}>
                      <td className="fw-medium">{d.student_name}</td>
                      <td style={{ fontSize: 12 }}>{d.class_grade}{d.section ? ` - ${d.section}` : ''}</td>
                      <td>
                        <span className="badge" style={{ background: '#e0f2fe', color: '#0369a1', fontSize: 11 }}>
                          {d.request_type}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{d.from_date ? new Date(d.from_date).toLocaleDateString() : '—'}</td>
                      <td style={{ fontSize: 12 }}>{d.to_date ? new Date(d.to_date).toLocaleDateString() : '—'}</td>
                      <td style={{ maxWidth: 150, fontSize: 12 }}>{d.reason}</td>
                      <td>
                        <span className="badge"
                          style={{ background: `${STATUS_COLORS[st] || '#6c757d'}20`, color: STATUS_COLORS[st] || '#6c757d', fontSize: 11 }}>
                          {st}
                        </span>
                      </td>
                      <td>
                        {st === 'Pending' && (
                          <div className="d-flex gap-1">
                            <button className="btn btn-sm btn-outline-success" style={{ padding: '2px 6px', fontSize: 11 }}
                              onClick={() => handleStatus(req._id, 'Approved')} title="Approve">✓</button>
                            <button className="btn btn-sm btn-outline-danger" style={{ padding: '2px 6px', fontSize: 11 }}
                              onClick={() => handleStatus(req._id, 'Rejected')} title="Reject">✗</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
